import {createContext, memo, useCallback, useMemo, useState} from 'react'

import type {IssueField} from '../../api/common-contracts'
import {apiGetSuggestedIssueFields} from '../../api/memex/api-get-suggested-issue-fields'
import {getInitialState} from '../../helpers/initial-state'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useAllColumns} from '../columns/use-all-columns'

type IssueFieldLoadingState = 'idle' | 'loading' | 'error' | 'success'

export type IssueFieldsContextType = {
  /** Indicates whether issue fields feature is enabled and the user is in an organization context */
  issueFieldsEnabled: boolean

  /** Load issue field suggestions */
  loadIssueFieldSuggestions: () => Promise<void>

  /** Suggested issue fields for an organization */
  suggestedIssueFields: Array<IssueField>

  /** Loading state for issue suggestions */
  suggestionsLoadingState: IssueFieldLoadingState

  /** Indicates whether issue field suggestions should be loaded */
  shouldLoadIssueFieldSuggestions: boolean
}

export const IssueFieldsContext = createContext<IssueFieldsContextType | null>(null)

export const IssueFieldsStateProvider = memo<{children: React.ReactNode}>(function IssueFieldsStateProvider({
  children,
}) {
  const [suggestionsLoadingState, setSuggestionsLoadingState] = useState<IssueFieldLoadingState>('idle')
  const {project_issue_fields} = useEnabledFeatures()
  const {isOrganization} = getInitialState()
  const issueFieldsEnabled = Boolean(project_issue_fields && isOrganization)
  const shouldLoadIssueFieldSuggestions = suggestionsLoadingState === 'idle' && issueFieldsEnabled

  const [issueFields, setIssueFields] = useState<Array<IssueField>>([])
  const {allColumns} = useAllColumns()

  const loadIssueFieldSuggestions = useCallback(async () => {
    if (suggestionsLoadingState === 'loading') return

    setSuggestionsLoadingState('loading')

    try {
      const res = await apiGetSuggestedIssueFields()
      setIssueFields(res.issue_fields)
      setSuggestionsLoadingState('success')
    } catch {
      setSuggestionsLoadingState('error')
    }
  }, [suggestionsLoadingState])

  const suggestedIssueFields = useMemo(() => {
    const importedIssueFieldIds = new Set(
      allColumns.filter(column => column.issueFieldId).map(column => column.issueFieldId),
    )

    return issueFields.filter(field => !importedIssueFieldIds.has(field.id))
  }, [issueFields, allColumns])

  const contextValue: IssueFieldsContextType = useMemo(() => {
    return {
      loadIssueFieldSuggestions,
      suggestionsLoadingState,
      suggestedIssueFields,
      issueFieldsEnabled,
      shouldLoadIssueFieldSuggestions,
    }
  }, [
    loadIssueFieldSuggestions,
    suggestionsLoadingState,
    suggestedIssueFields,
    issueFieldsEnabled,
    shouldLoadIssueFieldSuggestions,
  ])

  return <IssueFieldsContext.Provider value={contextValue}>{children}</IssueFieldsContext.Provider>
})

try{ IssueFieldsContext.displayName ||= 'IssueFieldsContext' } catch {}
try{ IssueFieldsStateProvider.displayName ||= 'IssueFieldsStateProvider' } catch {}